import { resolveIntroAuthPlatform } from "@components/SignInIntro/resolveIntroAuthPlatform";

/**
 * 归因四字段（PRD 附录 B）：`ad_source` / `ad_campaign_id` / `ad_creative_id` / `platform`。
 *
 * 为什么每个事件都要自带：`tracking-conventions.md` §3 的 envelope 保留键里**没有**归因字段，
 * 不传就没有。Web 端拿不到 `ad_campaign_id` / `ad_creative_id`（Adjust attribution 只在原生侧可读，
 * 与 `adChatTrack.ts` 同口径），缺省不带该字段 —— 宁可缺字段也不造值。
 *
 * ⚠️ 事件名必须写在调用点的字面量上（`gen-openreplay-events.mjs` 靠静态扫描），所以本文件
 * **不提供** `emit(name)` 这类透传函数；参数 key 只能小写。
 */
// type 而非 interface：只有 type 别名才能赋给 `TrackParams` 的字符串索引签名
export type OverlapAttributionParams = {
  /** 本链路固定小写 `etf_overlap`（附录 B）；store / cookie 里的值是大写 `ETF_OVERLAP`。 */
  ad_source: "etf_overlap";
  /** `ios` / `android` / `web`（附录 B）；`resolveIntroAuthPlatform()` 的 `pc` 即 web。 */
  platform: "ios" | "android" | "web";
  ad_campaign_id?: string;
  ad_creative_id?: string;
};

const resolvePlatform = (): OverlapAttributionParams["platform"] => {
  const p = resolveIntroAuthPlatform();
  return p === "pc" ? "web" : p;
};

/** 命令式：`Track.event("overlap_xxx", { ...overlapAttributionParams(), ...payload })`。 */
export function overlapAttributionParams(): OverlapAttributionParams {
  return { ad_source: "etf_overlap", platform: resolvePlatform() };
}

/**
 * 声明式：展开到与 `data-or-event` / `data-or-view` **同一元素**上
 * （`data-or-param-*` 必须与它们同元素，key 只能小写，值会变成字符串）。
 */
export function overlapAttributionDataProps(): Record<
  `data-or-param-${string}`,
  string
> {
  const p = overlapAttributionParams();
  return {
    "data-or-param-ad_source": p.ad_source,
    "data-or-param-platform": p.platform,
    ...(p.ad_campaign_id
      ? { "data-or-param-ad_campaign_id": p.ad_campaign_id }
      : {}),
    ...(p.ad_creative_id
      ? { "data-or-param-ad_creative_id": p.ad_creative_id }
      : {}),
  };
}
